/**
 * Stripe-only “lucky watcher” discount: random reduction for users who engaged with video flow.
 * Server clamps `watcherLuckDiscountPercent` to env VIDEO_WATCHER_LUCK_DISCOUNT_MAX_PERCENT.
 *
 * Frontend also needs `VITE_VIDEO_WATCHER_LUCK_DISCOUNT_MAX_PERCENT` (same numeric value as Railway)
 * so the client can roll the dice before checkout. If unset / 0, feature is off.
 */

export const STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT = 25; // Must match api/create-payment-intent (exclusive pack)

export const WATCHER_LUCK_SESSION_STORAGE_KEY = "stripe_watcher_luck_pct_v1";

function parsePositiveInt(raw: string | undefined): number {
  if (raw == null || raw.trim() === "") return 0;
  const n = Number.parseInt(raw.trim(), 10);
  if (!Number.isFinite(n) || n <= 0) return 0;
  return Math.min(n, 60);
}

/** Max percent (1–60) rolled on the client; must mirror VITE_ value in Railway. */
export function getWatcherLuckDiscountMaxPercentClient(): number {
  if (typeof import.meta.env.VITE_VIDEO_WATCHER_LUCK_DISCOUNT_MAX_PERCENT === "undefined")
    return 0;
  return parsePositiveInt(
    String(import.meta.env.VITE_VIDEO_WATCHER_LUCK_DISCOUNT_MAX_PERCENT ?? ""),
  );
}

/** Random inclusive [min, max]; min ramps with max so tiny cuts are avoided when max is large. */
export function pickWatcherLuckDiscountPercent(max: number): number {
  if (max <= 0) return 0;
  const cap = Math.min(max, 60);
  const min = Math.min(cap, Math.max(1, Math.ceil(cap * 0.22)));
  const lo = Math.min(min, cap);
  const hi = Math.max(lo, cap);
  return Math.floor(Math.random() * (hi - lo + 1)) + lo;
}

export function readSessionWatcherLuckPercent(): number | null {
  if (typeof sessionStorage === "undefined") return null;
  try {
    const raw = sessionStorage.getItem(WATCHER_LUCK_SESSION_STORAGE_KEY);
    if (raw == null) return null;
    const n = Number.parseInt(raw, 10);
    if (!Number.isFinite(n) || n <= 0) return null;
    return Math.min(60, n);
  } catch {
    return null;
  }
}

export function writeSessionWatcherLuckPercent(percent: number): void {
  if (typeof sessionStorage === "undefined" || percent <= 0) return;
  try {
    sessionStorage.setItem(WATCHER_LUCK_SESSION_STORAGE_KEY, String(percent));
  } catch {
    /* quota / privacy mode */
  }
}

/**
 * Persist one roll per tab session when feature is enabled and Stripe packs may apply.
 */
export function getOrCreateSessionWatcherLuckPercent(): number {
  const max = getWatcherLuckDiscountMaxPercentClient();
  if (max <= 0) return 0;
  const existing = readSessionWatcherLuckPercent();
  if (existing != null && existing <= max) return existing;
  const rolled = pickWatcherLuckDiscountPercent(max);
  writeSessionWatcherLuckPercent(rolled);
  return rolled;
}

export interface StripePackCheckoutAmountsInput {
  baseAmountCents: number;
  packId: string;
  /** 0 = none */
  watcherLuckPercent: number;
}

/** Amounts aligned with PaymentIntent (exclusive 25%, then luck on that subtotal). */
export function computeStripePackCheckoutAmounts(input: StripePackCheckoutAmountsInput): {
  finalAmountCents: number;
  amountAfterExclusiveCents: number;
  originalListAmountCents: number;
  showDiscountBadge: boolean;
  /** For StripeCheckoutForm (effective savings vs list price) */
  discountPercentDisplay: number | undefined;
} {
  const { baseAmountCents: base, packId, watcherLuckPercent } = input;
  const maxClient = getWatcherLuckDiscountMaxPercentClient();
  const exclusive =
    packId === "exclusive" ? STRIPE_EXCLUSIVE_PACK_DISCOUNT_PERCENT : 0;
  const afterExclusive =
    exclusive > 0
      ? Math.round(base * (1 - exclusive / 100))
      : base;
  /** Match api/create-payment-intent: floor body %, clamp by configured max (0 = disabled). */
  let luck =
    Number.isFinite(watcherLuckPercent) && watcherLuckPercent > 0
      ? Math.max(0, Math.min(60, Math.floor(watcherLuckPercent)))
      : 0;
  if (maxClient <= 0) {
    luck = 0;
  } else if (luck > 0) {
    luck = Math.min(luck, maxClient);
  }
  const finalAmountCents =
    luck > 0
      ? Math.round(afterExclusive * (1 - luck / 100))
      : afterExclusive;

  const hasSavings = finalAmountCents > 0 && finalAmountCents < base;
  const discountPercentDisplay = hasSavings
    ? Math.max(1, Math.round(100 - (finalAmountCents / base) * 100))
    : undefined;

  return {
    finalAmountCents,
    amountAfterExclusiveCents: afterExclusive,
    originalListAmountCents: base,
    showDiscountBadge: Boolean(hasSavings && discountPercentDisplay != null),
    discountPercentDisplay,
  };
}
